import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { MOCK_PAYMENT_METHODS } from '../mock';
import { CustomerPaymentMethod } from '../types';
import type { PaymentMethodFormData } from '../schemas/paymentMethod';

/* ================ PAYMENT METHOD - STORE (STATE) ================ */

interface PaymentMethodStoreState {
  methods: CustomerPaymentMethod[];
  isLoading: boolean;
  error: string | null;

  // QUERIES
  getMethodsByCustomer: (customer_id: string) => CustomerPaymentMethod[];
  getDefaultMethodForCustomer: (customer_id: string) => CustomerPaymentMethod | undefined;

  // CURD
  addMethod: (customer_id: string, data: PaymentMethodFormData) => CustomerPaymentMethod;
  updateMethod: (
    method_id: string,
    updates: Partial<Omit<CustomerPaymentMethod, 'method'>>
  ) => void;
  deleteMethod: (method_id: string) => void;

  // UTILS
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

/* ================ PAYMENT METHOD - STORE (DEFAULT) ================ */
export const usePaymentMethodStore = create<PaymentMethodStoreState>()(
  devtools(
    (set, get) => ({
      methods: process.env.NODE_ENV === 'development' ? [...MOCK_PAYMENT_METHODS] : [],
      isLoading: false,
      error: null,

      // QUERIES
      getMethodsByCustomer: (customer_id) =>
        get().methods.filter((method) => method.customer_id === customer_id),

      getDefaultMethodForCustomer: (customer_id) =>
        get().methods.find((method) => method.customer_id === customer_id && method.isDefault),

      // CURD
      addMethod: (customer_id, data) => {
        const isFirstForCustomer = !get().methods.some(
          (method) => method.customer_id === customer_id
        );

        const newMethod: CustomerPaymentMethod = {
          ...data,
          id: `pm_${crypto.randomUUID() as string}`,
          customer_id,
          isDefault: isFirstForCustomer,
          createdAt: new Date().toISOString(),
        };
        set((state) => ({ methods: [...state.methods, newMethod] }));
        return newMethod;
      },

      updateMethod: (method_id, updates) => {
        set((state) => ({
          methods: state.methods.map((method) =>
            method.id === method_id ? { ...method, ...updates } : method
          ),
        }));
      },

      deleteMethod: (method_id) => {
        set((state) => ({
          methods: state.methods.filter((method) => method.id !== method_id),
        }));
      },

      // UTILS
      setLoading: (loading) => set((state) => ({ isLoading: loading })),
      setError: (error) => set({ error }),
    }),
    {
      name: 'payment-method-store',
    }
  )
);
